'use client';
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  UserPlus, UserCog, Trash2, Shield, Mail, Loader2, Search, Check, X,
  Users, Clock, CheckCircle2,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { userAdminService } from '@/lib/services/thesisService';

type Role = 'student' | 'librarian' | 'admin';
type Provider = 'any' | 'google' | 'azure';

export default function UserManagement() {
  const { user } = useAuth();
  const [tab, setTab] = useState<'users' | 'preauth'>('users');

  // Users
  const [users, setUsers] = useState<any[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [userSearch, setUserSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<'all' | Role>('all');
  const [savingRoleId, setSavingRoleId] = useState<string | null>(null);

  // Pre-authorization
  const [preauth, setPreauth] = useState<any[]>([]);
  const [loadingPre, setLoadingPre] = useState(true);
  const [newEmail, setNewEmail] = useState('');
  const [newProvider, setNewProvider] = useState<Provider>('any');
  const [newRole, setNewRole] = useState<Exclude<Role, 'student'>>('librarian');
  const [newNote, setNewNote] = useState('');
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    const [u, p] = await Promise.all([
      userAdminService.listUsers(),
      userAdminService.listPreAuthorized(),
    ]);
    setUsers(u);
    setPreauth(p);
    setLoadingUsers(false);
    setLoadingPre(false);
  };

  const filteredUsers = useMemo(() => {
    const q = userSearch.trim().toLowerCase();
    return users.filter((u) => {
      const matchSearch = !q || u.email?.toLowerCase().includes(q) || u.full_name?.toLowerCase().includes(q);
      const matchRole = roleFilter === 'all' || u.role === roleFilter;
      return matchSearch && matchRole;
    });
  }, [users, userSearch, roleFilter]);

  const handleChangeRole = async (userId: string, newRoleValue: Role) => {
    setSavingRoleId(userId);
    try {
      const ok = await userAdminService.setUserRole(userId, newRoleValue);
      if (ok) {
        setUsers((prev) => prev.map((u) => (u.id === userId ? { ...u, role: newRoleValue } : u)));
        toast.success(`Role updated to ${newRoleValue}.`);
      } else {
        toast.error('Failed to update role.');
      }
    } catch (err: any) {
      toast.error(err?.message || 'Failed to update role.');
    } finally {
      setSavingRoleId(null);
    }
  };

  const handleAddPreauth = async () => {
    if (!newEmail.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail)) {
      toast.error('Please enter a valid email address.');
      return;
    }
    setAdding(true);
    try {
      const created = await userAdminService.addPreAuthorized({
        email: newEmail,
        provider: newProvider,
        role: newRole,
        note: newNote,
        addedBy: user?.id,
      });
      if (created) {
        setPreauth((prev) => [created, ...prev]);
        toast.success(`Pre-authorized ${newEmail} as ${newRole}.`);
        setNewEmail('');
        setNewNote('');
        // Refresh users list in case the existing user was elevated
        loadAll();
      }
    } catch (err: any) {
      toast.error(err?.message || 'Failed to pre-authorize. The email may already be registered.');
    } finally {
      setAdding(false);
    }
  };

  const handleRemovePreauth = async (id: string) => {
    const ok = await userAdminService.removePreAuthorized(id);
    if (ok) {
      setPreauth((prev) => prev.filter((p) => p.id !== id));
      toast.success('Pre-authorization removed.');
    } else {
      toast.error('Failed to remove pre-authorization.');
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-xl font-700 text-[#0f1a35]">User Management</h2>
          <p className="text-sm text-[#4a5578] mt-0.5">
            Manage librarians and administrators, or pre-authorize a Google / Azure AD email to be granted a role on first login.
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 mb-4 border-b border-[#e2e6f0]">
        <button
          onClick={() => setTab('users')}
          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-600 border-b-2 transition-all ${
            tab === 'users' ? 'border-[#1a2e5a] text-[#1a2e5a]' : 'border-transparent text-[#8892a8] hover:text-[#4a5578]'
          }`}
        >
          <Users size={14} />
          Existing Users ({users.length})
        </button>
        <button
          onClick={() => setTab('preauth')}
          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-600 border-b-2 transition-all ${
            tab === 'preauth' ? 'border-[#1a2e5a] text-[#1a2e5a]' : 'border-transparent text-[#8892a8] hover:text-[#4a5578]'
          }`}
        >
          <Clock size={14} />
          Pre-authorized emails ({preauth.length})
        </button>
      </div>

      {/* USERS */}
      {tab === 'users' && (
        <div>
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <div className="relative flex-1 min-w-48">
              <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8892a8]" />
              <input
                type="text"
                placeholder="Search by name or email..."
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
                className="w-full pl-8 pr-3 py-2 text-xs border border-[#e2e6f0] rounded-lg bg-white focus:outline-none focus:border-[#1a2e5a]"
              />
            </div>
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value as any)}
              className="text-xs border border-[#e2e6f0] rounded-lg px-3 py-2 bg-white focus:outline-none focus:border-[#1a2e5a]"
            >
              <option value="all">All roles</option>
              <option value="student">Students</option>
              <option value="librarian">Librarians</option>
              <option value="admin">Admins</option>
            </select>
          </div>

          {loadingUsers ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 size={24} className="animate-spin text-[#1a2e5a]" />
            </div>
          ) : (
            <div className="bg-white border border-[#e2e6f0] rounded-2xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-[#e2e6f0] bg-[#f7f8fc]">
                      <th className="px-4 py-3 text-left font-600 text-[#4a5578] uppercase tracking-wide">Name</th>
                      <th className="px-4 py-3 text-left font-600 text-[#4a5578] uppercase tracking-wide">Email</th>
                      <th className="px-4 py-3 text-left font-600 text-[#4a5578] uppercase tracking-wide">Department</th>
                      <th className="px-4 py-3 text-left font-600 text-[#4a5578] uppercase tracking-wide">Role</th>
                      <th className="px-4 py-3 text-right font-600 text-[#4a5578] uppercase tracking-wide">Change Role</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.length === 0 ? (
                      <tr><td colSpan={5} className="px-4 py-12 text-center text-[#8892a8]">No users found.</td></tr>
                    ) : filteredUsers.map((u) => (
                      <tr key={u.id} className="border-b border-[#f0f2f8] hover:bg-[#f7f8fc]">
                        <td className="px-4 py-3 text-[#0f1a35] font-500">{u.full_name || '—'}</td>
                        <td className="px-4 py-3 text-[#4a5578]">{u.email}</td>
                        <td className="px-4 py-3 text-[#4a5578]">{u.department || '—'}</td>
                        <td className="px-4 py-3">
                          <RoleBadge role={u.role} />
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-2">
                            {savingRoleId === u.id ? (
                              <Loader2 size={14} className="animate-spin text-[#1a2e5a]" />
                            ) : (
                              <select
                                value={u.role}
                                onChange={(e) => handleChangeRole(u.id, e.target.value as Role)}
                                disabled={u.id === user?.id}
                                className="text-xs border border-[#e2e6f0] rounded-lg px-2.5 py-1.5 bg-white focus:outline-none focus:border-[#1a2e5a] disabled:opacity-50"
                                title={u.id === user?.id ? "You can't change your own role" : ''}
                              >
                                <option value="student">Student</option>
                                <option value="librarian">Librarian</option>
                                <option value="admin">Admin</option>
                              </select>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* PRE-AUTHORIZED */}
      {tab === 'preauth' && (
        <div>
          {/* Add form */}
          <div className="bg-white border border-[#e2e6f0] rounded-2xl p-5 mb-5">
            <h3 className="text-sm font-700 text-[#0f1a35] mb-3 flex items-center gap-2">
              <UserPlus size={14} className="text-[#1a2e5a]" />
              Pre-authorize a new librarian / admin
            </h3>
            <p className="text-xs text-[#4a5578] mb-4">
              The email you add below will automatically be granted the selected role the next time
              that person signs in with Google or Azure AD — or the role will be applied immediately
              if the person is already registered.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div className="md:col-span-2">
                <label className="text-xs font-600 text-[#4a5578] uppercase tracking-wide mb-1 block">Email</label>
                <input
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="person@university.edu"
                  className="w-full px-3 py-2 text-xs border border-[#e2e6f0] rounded-lg bg-white focus:outline-none focus:border-[#1a2e5a]"
                />
              </div>
              <div>
                <label className="text-xs font-600 text-[#4a5578] uppercase tracking-wide mb-1 block">Provider</label>
                <select
                  value={newProvider}
                  onChange={(e) => setNewProvider(e.target.value as Provider)}
                  className="w-full px-3 py-2 text-xs border border-[#e2e6f0] rounded-lg bg-white focus:outline-none focus:border-[#1a2e5a]"
                >
                  <option value="any">Any (Google or Azure AD)</option>
                  <option value="google">Google only</option>
                  <option value="azure">Azure AD only</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-600 text-[#4a5578] uppercase tracking-wide mb-1 block">Role</label>
                <select
                  value={newRole}
                  onChange={(e) => setNewRole(e.target.value as any)}
                  className="w-full px-3 py-2 text-xs border border-[#e2e6f0] rounded-lg bg-white focus:outline-none focus:border-[#1a2e5a]"
                >
                  <option value="librarian">Librarian</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div className="md:col-span-3">
                <label className="text-xs font-600 text-[#4a5578] uppercase tracking-wide mb-1 block">Note (optional)</label>
                <input
                  type="text"
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  placeholder="e.g. Library head — authorized by Dean on 2026-04-10"
                  className="w-full px-3 py-2 text-xs border border-[#e2e6f0] rounded-lg bg-white focus:outline-none focus:border-[#1a2e5a]"
                />
              </div>
              <div className="md:col-span-1 flex items-end">
                <button
                  onClick={handleAddPreauth}
                  disabled={adding || !newEmail.trim()}
                  className="w-full flex items-center justify-center gap-2 bg-[#1a2e5a] hover:bg-[#111e3d] text-white text-xs font-600 px-4 py-2 rounded-lg transition-all disabled:opacity-50"
                >
                  {adding ? <Loader2 size={13} className="animate-spin" /> : <UserPlus size={13} />}
                  Add
                </button>
              </div>
            </div>
          </div>

          {/* List */}
          {loadingPre ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 size={24} className="animate-spin text-[#1a2e5a]" />
            </div>
          ) : (
            <div className="bg-white border border-[#e2e6f0] rounded-2xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-[#e2e6f0] bg-[#f7f8fc]">
                      <th className="px-4 py-3 text-left font-600 text-[#4a5578] uppercase tracking-wide">Email</th>
                      <th className="px-4 py-3 text-left font-600 text-[#4a5578] uppercase tracking-wide">Role</th>
                      <th className="px-4 py-3 text-left font-600 text-[#4a5578] uppercase tracking-wide">Provider</th>
                      <th className="px-4 py-3 text-left font-600 text-[#4a5578] uppercase tracking-wide">Note</th>
                      <th className="px-4 py-3 text-left font-600 text-[#4a5578] uppercase tracking-wide">Status</th>
                      <th className="px-4 py-3 text-right font-600 text-[#4a5578] uppercase tracking-wide">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preauth.length === 0 ? (
                      <tr><td colSpan={6} className="px-4 py-12 text-center text-[#8892a8]">No pre-authorized emails yet.</td></tr>
                    ) : preauth.map((p) => (
                      <tr key={p.id} className="border-b border-[#f0f2f8] hover:bg-[#f7f8fc]">
                        <td className="px-4 py-3 text-[#0f1a35] font-500">
                          <div className="flex items-center gap-2">
                            <Mail size={12} className="text-[#8892a8]" />
                            {p.email}
                          </div>
                        </td>
                        <td className="px-4 py-3"><RoleBadge role={p.role} /></td>
                        <td className="px-4 py-3 text-[#4a5578] capitalize">{p.provider}</td>
                        <td className="px-4 py-3 text-[#4a5578] max-w-xs truncate">{p.note || '—'}</td>
                        <td className="px-4 py-3">
                          {p.claimed_at ? (
                            <span className="inline-flex items-center gap-1 text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5 font-600">
                              <CheckCircle2 size={10} /> Claimed
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5 font-600">
                              <Clock size={10} /> Awaiting first login
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button
                            onClick={() => handleRemovePreauth(p.id)}
                            className="p-1.5 text-[#8892a8] hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                            title="Remove pre-authorization"
                          >
                            <Trash2 size={13} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function RoleBadge({ role }: { role: Role }) {
  const cfg = role === 'admin'
    ? { label: 'Admin', cls: 'bg-purple-50 text-purple-700 border-purple-200', icon: Shield }
    : role === 'librarian'
    ? { label: 'Librarian', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: UserCog }
    : { label: 'Student', cls: 'bg-gray-50 text-gray-600 border-gray-200', icon: Users };
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-600 border rounded-full px-2 py-0.5 ${cfg.cls}`}>
      <Icon size={10} />
      {cfg.label}
    </span>
  );
}
